import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import apiClient from '../api/client';
import { useToast } from '../components/ui/Toast';
import { Skeleton } from '../components/ui/Skeleton';
import { formatEUR } from '../lib/format';
import { enumLabel } from '../lib/enumLabel';

interface Addr {
  line1?: string;
  line2?: string;
  postalCode?: string;
  city?: string;
  country?: string;
}
interface Account {
  id: string;
  name: string;
  customerType: string;
  siret: string | null;
  vatNumber: string | null;
  contact: Record<string, unknown> | null;
  billingAddress: Addr | null;
  shippingAddress: Addr | null;
  emails: {
    invoice: string | null;
    packingSlip: string | null;
    quotation: string | null;
    order: string | null;
  };
  iban: string | null;
  website: string | null;
  billingType: string | null;
  creditPeriod: number | null;
  credit: { limit: number; outstandingBalance: number; reserved: number; available: number };
  /** The account's pickup code — the one every order of the company carries (migration 169). */
  pickupCode?: string | null;
}

/**
 * B2B account page (`/b2b/account`) — the company's own profile and credit standing.
 * Read-only; profile edits stay with internal relation staff. Data is scoped server-side
 * to the caller's company (GET /b2b/account).
 */
export default function CustomerAccount() {
  const { t } = useTranslation();
  const { showApiError } = useToast();
  const [acct, setAcct] = useState<Account | null>(null);

  useEffect(() => {
    apiClient
      .get<Account>('/b2b/account')
      .then((r) => setAcct(r.data))
      .catch(showApiError);
  }, [showApiError]);

  if (!acct) return <Skeleton className="h-40 w-full" />;

  const contact = (acct.contact ?? {}) as Record<string, unknown>;
  const bill = acct.billingAddress ?? {};
  const ship = acct.shippingAddress ?? {};
  const s = (v: unknown) => (v == null || v === '' ? null : String(v));
  const usedPct =
    acct.credit.limit > 0
      ? Math.min(
          100,
          Math.round(
            ((acct.credit.outstandingBalance + acct.credit.reserved) / acct.credit.limit) * 100
          )
        )
      : 0;
  const overLimit = acct.credit.outstandingBalance + acct.credit.reserved > acct.credit.limit;

  return (
    <div>
      <h1 className="page-title">{acct.name}</h1>
      <p className="page-sub">
        {t('portal.account.subtitle', { type: enumLabel('customerType', acct.customerType) })}
      </p>

      {/* Credit standing */}
      <section className="card" style={{ marginTop: 'var(--s-4)' }}>
        <h2 className="card-h">{t('portal.account.credit')}</h2>
        <div className="stat-grid">
          <Stat label={t('portal.account.creditLimit')} value={formatEUR(acct.credit.limit)} />
          <Stat
            label={t('portal.account.outstanding')}
            value={formatEUR(acct.credit.outstandingBalance)}
          />
          <Stat label={t('portal.account.reserved')} value={formatEUR(acct.credit.reserved)} />
          <Stat
            label={t('portal.account.available')}
            value={formatEUR(acct.credit.available)}
            tone={overLimit ? 'critical' : 'good'}
          />
        </div>
        {acct.credit.limit > 0 && (
          <div className="bar" title={t('portal.account.usedPct', { pct: usedPct })}>
            <i className={overLimit ? 'over' : undefined} style={{ width: `${usedPct}%` }} />
          </div>
        )}
      </section>

      <div className="cols2" style={{ marginTop: 'var(--s-4)' }}>
        {/* Company / tax */}
        <section className="card">
          <h2 className="card-h">{t('portal.account.company')}</h2>
          <dl>
            <Row label={t('portal.account.name')} value={acct.name} />
            <Row label={t('portal.account.siret')} value={s(acct.siret)} />
            <Row label={t('portal.account.vatNumber')} value={s(acct.vatNumber)} />
            <Row
              label={t('portal.account.billingType')}
              value={
                acct.billingType
                  ? t(`portal.account.billingTypes.${acct.billingType}`, {
                      defaultValue: acct.billingType,
                    })
                  : null
              }
            />
            <Row
              label={t('portal.account.creditPeriod')}
              value={
                acct.creditPeriod == null
                  ? null
                  : t('portal.account.days', { count: acct.creditPeriod })
              }
            />
            <Row label={t('portal.account.iban')} value={s(acct.iban)} />
            <Row label={t('portal.account.website')} value={s(acct.website)} />
            {acct.pickupCode && (
              <Row
                label={t('portal.account.pickupCode')}
                value={acct.pickupCode}
                hint={t('portal.account.pickupCodeHint')}
              />
            )}
          </dl>
        </section>

        {/* Contact */}
        <section className="card">
          <h2 className="card-h">{t('portal.account.contact')}</h2>
          <dl className="">
            <Row label={t('common.email')} value={s(contact.email)} />
            <Row label={t('common.phone')} value={s(contact.phone)} />
            <Row label={t('portal.account.emailInvoices')} value={s(acct.emails.invoice)} />
            <Row label={t('portal.account.emailPackingSlips')} value={s(acct.emails.packingSlip)} />
            <Row label={t('portal.account.emailQuotations')} value={s(acct.emails.quotation)} />
            <Row label={t('portal.account.emailOrders')} value={s(acct.emails.order)} />
          </dl>
        </section>

        {/* Billing address */}
        <section className="card">
          <h2 className="card-h">{t('portal.account.billingAddress')}</h2>
          <dl className="">
            <Row label={t('portal.account.address')} value={s(bill.line1)} />
            <Row label={t('portal.account.complement')} value={s(bill.line2)} />
            <Row label={t('portal.account.zipCode')} value={s(bill.postalCode)} />
            <Row label={t('common.city')} value={s(bill.city)} />
            <Row label={t('common.country')} value={s(bill.country)} />
          </dl>
        </section>

        {/* Delivery address */}
        <section className="card">
          <h2 className="card-h">{t('portal.account.deliveryAddress')}</h2>
          <dl className="">
            <Row label={t('portal.account.address')} value={s(ship.line1)} />
            <Row label={t('portal.account.complement')} value={s(ship.line2)} />
            <Row label={t('portal.account.zipCode')} value={s(ship.postalCode)} />
            <Row label={t('common.city')} value={s(ship.city)} />
            <Row label={t('common.country')} value={s(ship.country)} />
          </dl>
        </section>
      </div>

      <p
        className="muted"
        style={{ fontSize: 'var(--t-xs)', marginTop: 'var(--s-4)', textAlign: 'center' }}
      >
        {t('portal.account.footer')}
      </p>
    </div>
  );
}

function Stat({
  label,
  value,
  tone = 'default',
}: {
  label: string;
  value: string;
  tone?: 'default' | 'good' | 'critical';
}) {
  return (
    <div>
      <div className="stat-l">{label}</div>
      <div className={`stat-v ${tone}`}>{value}</div>
    </div>
  );
}

function Row({
  label,
  value,
  hint,
}: {
  label: string;
  value: string | null;
  /** One line under the value, for the one row that needs explaining (the pickup code). */
  hint?: string;
}): ReactNode {
  return (
    <div className="drow">
      <dt>{label}</dt>
      <dd title={value ?? undefined}>
        {value ?? (
          <span className="muted" style={{ opacity: 0.6 }}>
            —
          </span>
        )}
        {hint && (
          <div className="muted" style={{ fontSize: '0.8em', opacity: 0.7 }}>
            {hint}
          </div>
        )}
      </dd>
    </div>
  );
}
